/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/start-templates/qaStartTemplatesSelection.ts
 * FILE_ID: JOBHUB-QA-START-SELECTION-001
 * PURPOSE: Pure selection and payload for Prepare QA from the template library.
 */
import { classificationPayload, type QaClassification } from '../../../qa/classification'
import type { QaFromTemplatesPayload, QaTemplateDetail } from '../../../qa/qaTemplateTypes'
import type { QaTemplateLocationGroup } from '../templates/qaTemplateCatalog'

export type StartSelection = Record<string, Set<string>>
export type AreaNames = Record<string, string>

export function defaultSections(detail: QaTemplateDetail): string[] {
  return detail.sections
    .filter((section) => !section.is_optional && section.items.length > 0)
    .map((section) => section.id)
}

function drop(sel: StartSelection, templateId: string): StartSelection {
  const next = { ...sel }
  delete next[templateId]
  return next
}

export function toggleTemplate(sel: StartSelection, detail: QaTemplateDetail, on: boolean): StartSelection {
  if (!on) return drop(sel, detail.id)
  return { ...sel, [detail.id]: new Set(defaultSections(detail)) }
}

export function toggleSection(sel: StartSelection, templateId: string, sectionId: string): StartSelection {
  const current = new Set(sel[templateId] ?? [])
  if (current.has(sectionId)) current.delete(sectionId)
  else current.add(sectionId)
  if (current.size === 0) return drop(sel, templateId)
  return { ...sel, [templateId]: current }
}

export function toggleLocation(
  sel: StartSelection,
  group: QaTemplateLocationGroup,
  detailsById: Record<string, QaTemplateDetail>,
  on: boolean,
): StartSelection {
  let next = sel
  for (const template of group.templates) {
    const detail = detailsById[template.id]
    if (detail) next = toggleTemplate(next, detail, on)
  }
  return next
}

export function selectAll(
  groups: readonly QaTemplateLocationGroup[],
  detailsById: Record<string, QaTemplateDetail>,
): StartSelection {
  return groups.reduce((sel, group) => toggleLocation(sel, group, detailsById, true), {} as StartSelection)
}

export function clearAll(): StartSelection {
  return {}
}

export function triState(total: number, selected: number): 'all' | 'some' | 'none' {
  if (selected <= 0 || total <= 0) return 'none'
  if (selected >= total) return 'all'
  return 'some'
}

export function countStart(
  sel: StartSelection,
  groups: readonly QaTemplateLocationGroup[],
): { areas: number; checklists: number } {
  let areas = 0
  let checklists = 0
  for (const group of groups) {
    const chosen = group.templates.filter((template) => (sel[template.id]?.size ?? 0) > 0).length
    if (chosen > 0) {
      areas += 1
      checklists += chosen
    }
  }
  return { areas, checklists }
}

export function buildFromTemplatesPayload(
  groups: readonly QaTemplateLocationGroup[],
  sel: StartSelection,
  areaNames: AreaNames,
  detailsById: Record<string, QaTemplateDetail>,
  jobId: number,
  name: string,
  classification: QaClassification,
): QaFromTemplatesPayload {
  const areas = groups.flatMap((group) => {
    const templates = group.templates.flatMap((summary) => {
      const chosen = sel[summary.id]
      const detail = detailsById[summary.id]
      if (!chosen || chosen.size === 0 || !detail) return []
      const section_ids = detail.sections.map((section) => section.id).filter((id) => chosen.has(id))
      return section_ids.length ? [{ template_id: summary.id, section_ids }] : []
    })
    if (!templates.length) return []
    return [{
      name: areaNames[group.location_type]?.trim() || group.label,
      templates,
    }]
  })
  const trimmed = name.trim()
  return {
    job_id: jobId,
    ...(trimmed ? { inspection_name: trimmed } : {}),
    ...classificationPayload(classification),
    areas,
  }
}
